<script setup lang="ts">
import { onBeforeUnmount, watch } from "vue";
import { ref } from "vue";
import Button from "./Button.vue";
import { backdropClose } from "./backdrop-close";
import { isTopModal, modalDepth, modalZIndex, popModal, pushModal } from "./modal-stack";

const props = withDefaults(defineProps<{
  open: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
}>(), {
  title: "确认删除",
  message: "",
  confirmLabel: "删除",
});

const emit = defineEmits<{ cancel: []; confirm: [] }>();

// 点遮罩取消：按下和松开都必须在遮罩上（弹框里选文字拖出去松手不该关）
const backdrop = backdropClose(() => true, () => emit("cancel"));

// 确认弹框属于同一套弹框层级：它总是最后打开的，所以永远压在触发它的弹框上面，
// 并且 Esc 只关它自己（否则会把下面的弹框一起关掉）。
const token = Symbol("confirm");
const overlayZ = ref(50);

function onKey(event: KeyboardEvent) {
  if (!isTopModal(token)) return;
  if (event.key === "Escape") emit("cancel");
}

watch(() => props.open, (open) => {
  if (open) {
    pushModal(token);
    overlayZ.value = modalZIndex(modalDepth()) + 1; // 永远比触发它的弹框高一层
    window.addEventListener("keydown", onKey);
  } else {
    window.removeEventListener("keydown", onKey);
    popModal(token);
  }
}, { immediate: true });

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKey);
  popModal(token);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="ro-overlay confirm-overlay" role="presentation" :style="{ zIndex: overlayZ }" v-on="backdrop">
      <section class="ro-modal confirm-dialog" role="dialog" aria-modal="true" :aria-label="title">
        <header class="ro-modal__header">
          <strong>{{ title }}</strong>
          <Button class="ro-modal__close" variant="ghost" icon="x" type="button" aria-label="关闭" @click="emit('cancel')" />
        </header>
        <div class="ro-modal__body"><p>{{ message }}</p></div>
        <footer class="ro-modal__footer">
          <Button type="button" @click="emit('cancel')">取消</Button>
          <Button type="button" variant="danger" @click="emit('confirm')">{{ confirmLabel }}</Button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.confirm-overlay { z-index: 80; }
.confirm-dialog { width: min(420px, 93vw); }
.confirm-dialog p { margin: 0; }
</style>
